import { useSelector } from 'react-redux';
import { SYSTEM_MODULES } from '../config/modules.config';

export const usePermissions = () => {
  // We get the user from the global state (state.users.data)
  const user = useSelector((state: any) => state.users?.data || null);

  const checkPermission = (submoduleOrModule: string, requiredLevel: 'read' | 'manage', moduleKey?: string): boolean => {
    if (!user) return false;

    // Admin bypass ONLY in 'heredar' mode.
    // In 'personalizado' mode the admin must respect exactly the permissions
    // they configured (e.g. so they can hide 'sueldos' from themselves).
    if (user.role === 'admin' && (user.permissionMode || 'heredar') === 'heredar') return true;

    // 1. Check if the string passed is a top-level Module Key
    const systemModule = SYSTEM_MODULES.find(m => m.key === submoduleOrModule);
    if (systemModule) {
      // If it's a module, access is granted if they have access to ANY of its submodules
      return systemModule.submodules.some(sub => {
        // Find the permission directly for the submodule to avoid recursion back to this module check
        const permissions = user.permissionMode === 'heredar'
          ? user.roleId?.permissionId?.permissions
          : user.permissionId?.permissions;

        // FIX: Must filter by BOTH module AND submodule to avoid collisions
        // (e.g. 'gestion' exists in proyectos, plantillas, empresas and almacen)
        const perm = permissions?.find((p: any) => p.module === systemModule.key && p.submodule === sub.key);
        if (!perm) return false;

        const levelWeight: Record<string, number> = { 'none': 0, 'read': 1, 'manage': 2 };
        return (levelWeight[perm.accessLevel] || 0) >= levelWeight[requiredLevel];
      });
    }

    // 2. Standard Submodule Check
    const levelWeight: Record<string, number> = {
      'none': 0,
      'read': 1,
      'manage': 2
    };

    const neededWeight = levelWeight[requiredLevel];

    // --- START CLEAN PERMISSION SELECTOR ---
    const getPermissionsArray = (obj: any) => {
      if (!obj) return null;
      return Array.isArray(obj.permissions) ? obj.permissions : null;
    };

    let activePermissions: any[] | null = null;

    // Determine the source of permissions based on mode
    const mode = user.permissionMode || 'heredar';

    if (mode === 'heredar' && user.roleId) {
      activePermissions = getPermissionsArray(user.roleId.permissionId);
    } else if (user.permissionId) {
      activePermissions = getPermissionsArray(user.permissionId);
    }

    if (!activePermissions) return false;
    // --- END CLEAN PERMISSION SELECTOR ---

    // Search for the submodule in the active permissions set
    // If moduleKey is provided, filter by both module AND submodule to avoid collisions
    const perm = activePermissions.find((p: any) =>
      moduleKey
        ? p.module === moduleKey && p.submodule === submoduleOrModule
        : p.submodule === submoduleOrModule
    );

    if (perm) {
      const userWeight = levelWeight[perm.accessLevel] || 0;
      return userWeight >= neededWeight;
    }

    // Fallback logic for modules vs submodules
    // If we're looking for a submodule (e.g. 'menuProyectos') and it's not explicitly defined,
    // check if the user has access to the parent module (e.g., 'proyectos').
    // This handles old records in the DB where submodule was the same as module.
    // NOTE: this fallback is SKIPPED when an explicit moduleKey is provided, because in that
    // case the caller wants a strict check (e.g. 'sueldos' must NOT be granted just because the
    // user has 'empleados' in the same 'rrhh' module).
    const parentModule = SYSTEM_MODULES.find(m => m.submodules.some(s => s.key === submoduleOrModule));
    if (!moduleKey && parentModule && parentModule.key !== submoduleOrModule) {
      const modulePerm = activePermissions.find((p: any) => p.submodule === parentModule.key || p.module === parentModule.key);
      if (modulePerm) {
        return (levelWeight[modulePerm.accessLevel] || 0) >= neededWeight;
      }
    }

    return false;
  };

  const hasAccessTo = (submodule: string, module?: string) => checkPermission(submodule, 'read', module);
  const canManage = (submodule: string, module?: string) => checkPermission(submodule, 'manage', module);
  const hasPermission = (submodule: string, level: 'read' | 'manage', module?: string) => checkPermission(submodule, level, module);

  return {
    hasAccessTo,
    canManage,
    hasPermission,
    user
  };
};
